import { useEffect, useState } from 'react';
import { API_ENDPOINT, API_URL_PRODUCT_SERVICE } from '@/api/constant';
import ImagePreview from '@/components/common/media/ImagePreview';
import { PageInfo } from '@/interfaces/common.interface';
import { callApi, ThrowError } from '@/api/callApi';
import DragDropList from '@/components/common/commonTable/DragDropList';
import Layout from '@/components/Layout/Layout';
import { GetFilterUrl, showToastNotification } from '@/utility/helper';
import Pagination from '@/components/common/Pagination';
import Card from '@/components/UI/Card';
import {
  IproductResponse,
  IproductResponseList,
} from '@/interfaces/productresponse.interface';
import { Edit } from 'lucide-react';

interface StateProps {
  products: IproductResponse[];
  loading: boolean;
  saving: boolean;
  pageInfo: PageInfo;
}

const ProductManager = () => {
  const [isEditing, setIsEditing] = useState<{
    isEditing: boolean;
    id: string | null;
    order: number | null;
  }>({
    isEditing: false,
    id: null,
    order: null,
  });

  const [states, setStates] = useState<StateProps>({
    loading: false,
    products: [],
    saving: false,
    pageInfo: {
      total: 0,
      totalPages: 0,
      limit: 10,
      page: 1,
    },
  });

  const handleChangeState = (newState: Partial<StateProps>) => {
    setStates((prev) => ({
      ...prev,
      ...newState,
    }));
  };

  const getProducts = async () => {
    handleChangeState({ loading: true });
    try {
      const filterQuery = {
        sort: 'order_index',
        page: states.pageInfo.page,
        limit: states.pageInfo.limit,
      };
      const query = GetFilterUrl(filterQuery);
      const PATH = `${API_URL_PRODUCT_SERVICE}${API_ENDPOINT.products}?${query ? query : ''}`;
      const response = await callApi<IproductResponseList>(PATH, {
        method: 'GET',
      });
      const normalized = response.data
        .map((c) => ({
          ...c,
          order: c.orderIndex,
        }))
        .sort((a, b) => a.order - b.order);

      handleChangeState({
        products: normalized,
        pageInfo: response.pageInfo,
      });
    } catch (error) {
      ThrowError(error);
    } finally {
      handleChangeState({ loading: false });
    }
  };

  useEffect(() => {
    getProducts();
  }, [states.pageInfo.page, states.pageInfo.limit]);

  const handleChangePage = (page: number) => {
    handleChangeState({
      pageInfo: {
        ...states.pageInfo,
        page,
      },
    });
  };

  const handleChangePageSize = (limit: number) => {
    handleChangeState({
      pageInfo: {
        ...states.pageInfo,
        page: 1,
        limit,
      },
    });
  };

  const setProducts = (items: IproductResponse[]) => {
    handleChangeState({
      products: items.map((item, index) => ({
        ...item,
        orderIndex: index + 1,
      })),
    });
  };

  const saveOrder = async (payload: { productId: string; order: number }[]) => {
    handleChangeState({ saving: true });
    try {
      const PATH = `${API_URL_PRODUCT_SERVICE}${API_ENDPOINT.products}/order/index`;
      await callApi(PATH, {
        method: 'PATCH',
        data: { updates: payload },
      });
      showToastNotification('success', 'Product order saved successfully');
      getProducts();
    } catch (error) {
      ThrowError(error);
    } finally {
      handleChangeState({ saving: false });
    }
  };

  return (
    <Layout>
      <Card>
        <DragDropList
          title="Primary Products"
          data={states.products}
          isLoading={states.loading}
          getId={(item) => item.id}
          renderItem={(item) => {
            const url = item?.directImageUrl
              ? item?.directImageUrl
              : item?.media?.[0]?.url;
            return (
              <div className="flex items-center justify-between gap-3 w-full">
                <ImagePreview
                  src={`${API_URL_PRODUCT_SERVICE}/${url}`}
                  text={item.name}
                />
                <div className="w-32 flex items-center justify-between border p-1 border-dashed border-primary rounded-xl">
                  <input
                    onClick={(e) => e.stopPropagation()}
                    value={
                      isEditing.id === item.id
                        ? (isEditing.order ?? item.orderIndex)
                        : item.orderIndex
                    }
                    disabled={isEditing.id !== item.id}
                    onChange={(e) =>
                      setIsEditing((prev) => ({
                        ...prev,
                        order: Number(e.target.value),
                      }))
                    }
                    className="border-none outline-none w-14 text-center disabled:bg-transparent"
                  />
                  {isEditing.id === item.id ? (
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        saveOrder([
                          {
                            productId: String(isEditing.id),
                            order: Number(isEditing.order),
                          },
                        ]);
                        setIsEditing({
                          isEditing: false,
                          id: null,
                          order: null,
                        });
                      }}
                      className="btn-primary py-1! text-xs px-1!"
                    >
                      Save
                    </button>
                  ) : (
                    <button
                      onClick={() => {
                        if (isEditing.id !== item.id) {
                          setIsEditing({
                            isEditing: true,
                            id: item.id,
                            order: item.orderIndex,
                          });
                        }
                      }}
                    >
                      <Edit size={18} color="green" />
                    </button>
                  )}
                </div>
                <span className=" text-muted">#{item.orderIndex}</span>
              </div>
            );
          }}
          onReorder={setProducts}
          onSave={() =>
            saveOrder(
              states.products.map((c) => ({
                productId: c.id,
                order: c.orderIndex,
              })),
            )
          }
          saving={states.saving}
        />
        <Pagination
          isLoading={states.loading}
          paginationData={states.pageInfo}
          onPageChange={(page) => handleChangePage(page)}
          onPageSizeChange={handleChangePageSize}
        />
      </Card>
    </Layout>
  );
};

export default ProductManager;
